import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Upload, Check, RotateCcw, Calculator } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { Dialog, StatusPill, money } from '../../components/ui';
import { Segmented } from '../Financials/financeKit';

// SIL funding line, Weekly or Hourly (routes/silWeekly.js; Vserve SIL workflow v1.3 section 4).
// Weekly needs the service agreement (date + file) that covers weekly claiming and the typical
// schedule of supports. The weekly amount is funding ÷ days × 7, part weeks pro-rata, and can be
// edited from a chosen Monday; weeks already claimed never change.

const pad = (n) => String(n).padStart(2, '0');
const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromIso = (iso) => { const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d); };
const addDays = (iso, n) => { const d = fromIso(iso); d.setDate(d.getDate() + n); return isoOf(d); };
const mondayOf = (iso) => addDays(iso, -((fromIso(iso).getDay() + 6) % 7));
const au = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '');
// A date written out ("10 May 2026"): a picked-up date must never be read the wrong way round.
const longDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : '');
const errOf = (e, fb) => e?.response?.data?.message || fb;
const HOURS = [['weekday', 'Weekday'], ['evening', 'Evening'], ['night', 'Night'], ['saturday', 'Saturday'], ['sunday', 'Sunday'], ['public_holiday', 'Public holiday'], ['sleepover', 'Sleepovers']];
const CLAIM_TONE = { ready: 'info', submitted: 'info', invoiced: 'info', paid: 'success' };

export default function SilWeeklyDialog({ lineId, clientId, onClose, onSaved, initialMethod }) {
  const [data, setData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState('');
  const [method, setMethod] = useState('hourly');
  const [fromWeek, setFromWeek] = useState('');
  const [agreementDate, setAgreementDate] = useState('');
  const [agreementDocId, setAgreementDocId] = useState('');
  const [hours, setHours] = useState({});
  const [agreementLater, setAgreementLater] = useState(false);
  // The agreement date picked up from the chosen file: shown so the person checks it. A date the
  // person typed is never overwritten; one the dialog filled in itself is.
  const [dateNote, setDateNote] = useState(null);
  const dateAuto = useRef(false);
  const [edit, setEdit] = useState({ fromWeek: '', amount: '', reason: '' });
  const [preview, setPreview] = useState(null);
  // The parent passes onClose inline; a ref keeps load() from re-running on every parent render.
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; });

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/sil-weekly/lines/${lineId}`);
      setData(d);
      setMethod((m) => (m !== 'hourly' ? m : (initialMethod && (d.line.claimingMethod || 'hourly') === 'hourly' ? initialMethod : (d.line.claimingMethod || 'hourly'))));
      setAgreementDate(d.line.agreementDate || '');
      setAgreementDocId(d.line.agreementDocId || '');
      setHours(d.line.weeklyHours || {});
      const start = d.line.periodFrom || isoOf(new Date());
      const thisMonday = mondayOf(isoOf(new Date()));
      setFromWeek((cur) => cur || (d.line.weeklyFrom || mondayOf(start > thisMonday ? start : thisMonday)));
      const firstOpen = (d.schedule?.weeks || []).find((w) => w.source !== 'claimed' && w.weekStart >= thisMonday);
      setEdit((e) => ({ ...e, fromWeek: e.fromWeek || firstOpen?.weekStart || thisMonday }));
      setPreview(null);
    } catch (e) { toast.error(errOf(e, 'Could not load the funding line')); closeRef.current?.(); }
  }, [lineId, initialMethod]);
  const loadDocs = useCallback(async () => {
    try { const { data: c } = await api.get(`/clients/${clientId}`); setDocs(c.documents || []); } catch { setDocs([]); }
  }, [clientId]);
  useEffect(() => { load(); loadDocs(); }, [load, loadDocs]);

  const pickDate = async (docId) => {
    setDateNote(null);
    if (!docId) return;
    setBusy('date');
    try {
      const { data: r } = await api.post('/sil-weekly/agreement-date', { docId });
      if (r.suggested) {
        const typed = agreementDate && !dateAuto.current;
        if (!typed) { setAgreementDate(r.suggested); dateAuto.current = true; }
        const period = r.startDate || r.endDate ? ` Service period ${r.startDate ? longDate(r.startDate) : '…'} to ${r.endDate ? longDate(r.endDate) : '…'}.` : '';
        const either = r.ambiguous ? ` The document writes it with numbers only, so it could also mean ${longDate(r.alternative)}.` : '';
        setDateNote({ tone: r.ambiguous ? 'warn' : 'ok', alternative: !typed && r.ambiguous ? r.alternative : null, text: typed
          ? `The agreement says ${longDate(r.suggested)}${r.agreementDate ? '' : ' (its start date)'}; the date you entered is kept.${either}${period}`
          : `Picked up from the agreement${r.source === 'ai' ? ' (read by AI)' : ''}: ${longDate(r.suggested)}${r.agreementDate ? '' : ', its start date (no signing date found)'}.${either} Check it before saving.${period}` });
      } else setDateNote({ tone: 'warn', text: r.message || 'No date found in the document. Enter it from the signed agreement.' });
    } catch { setDateNote({ tone: 'warn', text: 'Could not read the date from the document. Enter it from the signed agreement.' }); }
    finally { setBusy(''); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy('upload');
    let id = null;
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('documentType', 'Service Agreement');
      const { data: doc } = await api.post(`/clients/${clientId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      id = doc?.id || doc?.document?.id;
      await loadDocs();
      if (id) setAgreementDocId(id);
      toast.success('Service agreement uploaded');
    } catch (e) { toast.error(errOf(e, 'Upload failed')); }
    finally { setBusy(''); }
    if (id) pickDate(id);
  };

  const saveSetup = async () => {
    setBusy('setup');
    try {
      const body = { agreementDate: agreementDate || null, agreementDocId: agreementDocId || null, weeklyHours: hours };
      if (method !== (data.line.claimingMethod || 'hourly')) Object.assign(body, { method, fromWeek, agreementLater });
      await api.put(`/sil-weekly/lines/${lineId}/method`, body);
      toast.success(method === 'weekly' ? `Claimed weekly from ${au(fromWeek)}` : 'Saved');
      await load(); onSaved?.();
    } catch (e) { toast.error(errOf(e, 'Could not save')); }
    finally { setBusy(''); }
  };

  const doPreview = async () => {
    setBusy('preview');
    try { const { data: p } = await api.post(`/sil-weekly/lines/${lineId}/preview`, { fromWeek: edit.fromWeek, weeklyAmount: edit.amount === '' ? null : Number(edit.amount) }); setPreview(p); }
    catch (e) { toast.error(errOf(e, 'Could not preview')); }
    finally { setBusy(''); }
  };
  const saveAmount = async (reset = false) => {
    setBusy(reset ? 'reset' : 'amount');
    try {
      const { data: r } = await api.post(`/sil-weekly/lines/${lineId}/weekly-amount`, { fromWeek: edit.fromWeek, weeklyAmount: reset ? null : Number(edit.amount), reason: edit.reason || (reset ? 'Reset to calculated' : '') });
      toast.success(r.pendingApproval ? 'Saved. Waiting for an admin to approve it (claims would exceed the funding).' : reset ? 'Back to the calculated amount' : 'Weekly amount saved');
      setEdit((e) => ({ ...e, amount: '', reason: '' }));
      await load(); onSaved?.();
    } catch (e) { toast.error(errOf(e, 'Could not save the amount')); if (e.response?.data?.preview) setPreview(e.response.data.preview); }
    finally { setBusy(''); }
  };
  const spread = async () => {
    setBusy('spread');
    try {
      const { data: r } = await api.post(`/sil-weekly/lines/${lineId}/spread-remaining`, { fromWeek: edit.fromWeek, reason: edit.reason || undefined });
      toast.success(r.pendingApproval ? 'Saved, waiting for approval' : 'Remaining balance spread over the weeks left');
      await load(); onSaved?.();
    } catch (e) { toast.error(errOf(e, 'Could not spread the balance')); }
    finally { setBusy(''); }
  };

  const l = data?.line;
  const s = preview || data?.schedule;
  const changed = l && method !== (l.claimingMethod || 'hourly');
  return (
    <Dialog open onClose={onClose} size="xl" title={l ? `SIL claiming: ${l.service}` : 'SIL claiming'}
      description={l ? `${l.supportItem || ''} · ${au(l.periodFrom)} to ${au(l.periodTo)} · funding ${l.budget != null ? money(l.budget) : 'not set'}` : ''}>
      {!data ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div> : (
        <div className="space-y-6">
          {/* 1. Claiming method + agreement + roster hours */}
          <section className="space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <p className="text-[13px] font-medium text-slate-700 mb-1.5">Claiming method</p>
                <Segmented ariaLabel="Claiming method" value={method} onChange={setMethod}
                  options={[{ value: 'weekly', label: 'Weekly' }, { value: 'hourly', label: 'Hourly' }]} />
              </div>
              {changed && (
                <div>
                  <label htmlFor="sil-from-week" className="block text-[13px] font-medium text-slate-700 mb-1.5">From the week of (Monday)</label>
                  <input id="sil-from-week" type="date" className="input-field !py-1.5 text-sm" value={fromWeek} onChange={(e) => e.target.value && setFromWeek(mondayOf(e.target.value))} />
                </div>
              )}
              <p className="text-[12px] text-slate-500 pb-1.5 flex-1 min-w-[220px]">
                {l.claimingMethod === 'weekly' ? `Claimed weekly ${l.weeklyFrom ? `from ${au(l.weeklyFrom)}` : ''}${l.weeklyTo ? ` to ${au(l.weeklyTo)}` : ''}. SIL shifts in that time are $0, covered by the weekly claim.`
                  : 'Each approved SIL shift is claimed by the hour. Weekly and hourly SIL are never mixed for one participant.'}
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="sil-ag-date" className="block text-[13px] font-medium text-slate-700 mb-1">Service agreement date {method === 'weekly' && <span className="text-rose-600">*</span>}</label>
                <input id="sil-ag-date" type="date" className="input-field !py-1.5 text-sm" value={agreementDate} onChange={(e) => { dateAuto.current = false; setAgreementDate(e.target.value); }} />
                {busy === 'date' && <p className="text-[12px] text-slate-500 mt-1 flex items-center gap-1"><Loader2 size={12} className="animate-spin" aria-hidden="true" /> Reading the date from the agreement…</p>}
                {dateNote && busy !== 'date' && (
                  <p role="status" className={`text-[12px] mt-1 ${dateNote.tone === 'ok' ? 'text-teal-700' : 'text-amber-700'}`}>
                    {dateNote.text}
                    {dateNote.alternative && (
                      <button type="button" className="ml-1 font-semibold underline hover:no-underline"
                        onClick={() => { setAgreementDate(dateNote.alternative); dateAuto.current = true; setDateNote({ tone: 'ok', text: `Set to ${longDate(dateNote.alternative)}. Save to keep it.` }); }}>
                        Use {longDate(dateNote.alternative)}
                      </button>
                    )}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="sil-ag-doc" className="block text-[13px] font-medium text-slate-700 mb-1">Service agreement file {method === 'weekly' && <span className="text-rose-600">*</span>}</label>
                <div className="flex gap-2">
                  <select id="sil-ag-doc" className="input-field !py-1.5 text-sm flex-1 min-w-0" value={agreementDocId} onChange={(e) => { setAgreementDocId(e.target.value); pickDate(e.target.value); }}>
                    <option value="">Choose a document…</option>
                    {docs.map((d) => <option key={d.id} value={d.id}>{d.name}{d.document_type ? ` (${d.document_type})` : ''}</option>)}
                  </select>
                  <label className="btn-secondary btn-sm cursor-pointer shrink-0" title="Upload the signed agreement">
                    {busy === 'upload' ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Upload size={13} aria-hidden="true" />} Upload
                    <input type="file" className="sr-only" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
                  </label>
                </div>
                <p className="text-[12px] text-slate-500 mt-1">Must cover weekly claiming and the agreed typical weekly schedule of supports.</p>
              </div>
            </div>
            <div>
              <p className="text-[13px] font-medium text-slate-700 mb-1.5">Weekly hours from the roster of care <span className="font-normal text-slate-500">(reference only, does not change the amount)</span></p>
              <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
                {HOURS.map(([k, label]) => (
                  <div key={k}>
                    <label htmlFor={`sil-h-${k}`} className="block text-[11px] text-slate-500 mb-0.5">{label}</label>
                    <input id={`sil-h-${k}`} type="number" min="0" step="0.5" className="input-field !py-1.5 text-sm" value={hours[k] ?? ''}
                      onChange={(e) => setHours((h) => ({ ...h, [k]: e.target.value === '' ? undefined : Number(e.target.value) }))} />
                  </div>
                ))}
              </div>
            </div>
            {l.claimingMethod === 'weekly' && (!l.agreementDate || !l.agreementDocId) && (
              <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-800">
                {!l.agreementDocId && !l.agreementDate ? 'The service agreement is not on file yet (no file and no date).'
                  : !l.agreementDate ? 'The service agreement file is attached, but its date is missing. Enter the date above and save.'
                  : 'The service agreement date is in, but the file is missing. Choose or upload the file above and save.'}
                {' '}Weekly claiming needs an agreement that covers weekly claiming and the typical weekly schedule of supports.
              </p>
            )}
            <div className="flex flex-wrap items-center justify-end gap-3">
              {changed && method === 'weekly' && (!agreementDate || !agreementDocId) && (
                <label className="flex items-center gap-2 text-[13px] text-slate-700 mr-auto cursor-pointer">
                  <input type="checkbox" className="rounded border-slate-300 text-indigo-600" checked={agreementLater} onChange={(e) => setAgreementLater(e.target.checked)} />
                  Switch now, agreement to follow (admins only; shows as missing until attached)
                </label>
              )}
              <button type="button" className="btn-primary btn-sm" disabled={!!busy} onClick={saveSetup}>
                {busy === 'setup' ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Check size={13} aria-hidden="true" />}
                {changed ? (method === 'weekly' ? `Claim weekly from ${au(fromWeek)}` : `Claim hourly from ${au(fromWeek)}`) : 'Save'}
              </button>
            </div>
          </section>

          {/* 2. The weekly amount and its claim schedule */}
          {s?.ok === false && <p className="text-sm text-amber-700">{s.reason}</p>}
          {s?.ok && (
            <section className="space-y-3 pt-5 border-t border-slate-100">
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
                <Fig label="Days in period" value={s.days} />
                <Fig label="Daily amount" value={money(s.daily)} />
                <Fig label="Weekly amount" value={money(s.calcWeekly)} sub={s.currentWeekly !== s.calcWeekly ? `now ${money(s.currentWeekly)}` : 'calculated'} />
                <Fig label="Weeks in period" value={s.weeksInPeriod} />
                <Fig label="Projected claims" value={money(s.projected)} sub={s.variance ? `${s.variance > 0 ? 'over' : 'under'} by ${money(Math.abs(s.variance))}` : 'equals funding'} tone={s.variance > 0.005 ? 'rose' : null} />
                <Fig label="Balance left" value={data.remaining == null ? '–' : money(data.remaining)} />
              </div>
              {preview && <p className="text-[12px] text-indigo-700">Preview of the edit below, not saved yet.</p>}
              <div className="max-h-72 overflow-y-auto rounded-xl border border-slate-100">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-[12px] text-slate-500 sticky top-0">
                    <tr><th className="px-3 py-2 font-medium">Claim week</th><th className="px-3 py-2 font-medium">Days covered</th><th className="px-3 py-2 font-medium text-right">Claim</th><th className="px-3 py-2 font-medium">Status</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {s.weeks.map((w) => (
                      <tr key={w.weekStart} className={w.weekly ? '' : 'text-slate-400'}>
                        <td className="px-3 py-1.5 tabular-nums">{au(w.weekStart)} to {au(addDays(w.weekStart, 6))}</td>
                        <td className="px-3 py-1.5 tabular-nums">{w.days}{w.days < 7 ? ` (${au(w.from)} to ${au(w.to)})` : ''}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums font-medium">{money(w.amount)}</td>
                        <td className="px-3 py-1.5">
                          {w.claimStatus ? <StatusPill tone={CLAIM_TONE[w.claimStatus] || 'neutral'} label={`Claimed (${w.claimStatus})`} />
                            : !w.weekly ? <span className="text-[12px]">Hourly</span>
                            : <span className="text-[12px] text-slate-500">{w.source === 'edited' ? 'Edited amount' : 'Scheduled'}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="rounded-xl border border-slate-200 p-4 space-y-3">
                <p className="text-[13px] font-semibold text-slate-800">Change the weekly amount</p>
                <div className="flex flex-wrap items-end gap-3">
                  <div><label htmlFor="sil-e-week" className="block text-[12px] text-slate-500 mb-1">From the week of (Monday)</label>
                    <input id="sil-e-week" type="date" className="input-field !py-1.5 text-sm" value={edit.fromWeek} onChange={(e) => e.target.value && setEdit({ ...edit, fromWeek: mondayOf(e.target.value) })} /></div>
                  <div><label htmlFor="sil-e-amt" className="block text-[12px] text-slate-500 mb-1">New weekly amount ($)</label>
                    <input id="sil-e-amt" type="number" min="0" step="0.01" className="input-field !py-1.5 text-sm w-36" value={edit.amount} onChange={(e) => setEdit({ ...edit, amount: e.target.value })} /></div>
                  <div className="flex-1 min-w-[200px]"><label htmlFor="sil-e-why" className="block text-[12px] text-slate-500 mb-1">Reason (needed if claims would exceed the funding)</label>
                    <input id="sil-e-why" className="input-field !py-1.5 text-sm" value={edit.reason} onChange={(e) => setEdit({ ...edit, reason: e.target.value })} /></div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className="btn-secondary btn-sm" disabled={!!busy || edit.amount === ''} onClick={doPreview}>Preview</button>
                  <button type="button" className="btn-primary btn-sm" disabled={!!busy || edit.amount === ''} onClick={() => saveAmount(false)}>
                    {busy === 'amount' ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Check size={13} aria-hidden="true" />} Save amount
                  </button>
                  <button type="button" className="btn-secondary btn-sm" disabled={!!busy} onClick={() => saveAmount(true)} title="Funding amount ÷ days × 7 again from this week">
                    <RotateCcw size={13} aria-hidden="true" /> Reset to calculated
                  </button>
                  <button type="button" className="btn-secondary btn-sm" disabled={!!busy} onClick={spread} title="Weekly amount that uses exactly what is left over the weeks remaining">
                    <Calculator size={13} aria-hidden="true" /> Spread the remaining balance
                  </button>
                </div>
                <p className="text-[12px] text-slate-500">Weeks already claimed never change. A part week uses the new amount ÷ 7 a day. Every change is in the SIL audit log.</p>
              </div>

              {data.rates?.length > 0 && (
                <div className="text-[12px] text-slate-600 space-y-0.5">
                  <p className="font-medium text-slate-700">Changes</p>
                  {data.rates.map((r) => (
                    <p key={r.id}>From {au(r.fromWeek)}: {r.weeklyAmount == null ? 'calculated amount' : money(r.weeklyAmount)}{r.reason ? `, ${r.reason}` : ''} <span className="text-slate-400">({r.status.replace('_', ' ')})</span></p>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </Dialog>
  );
}

function Fig({ label, value, sub, tone }) {
  return (
    <div className={`rounded-xl border px-3 py-2 ${tone === 'rose' ? 'border-rose-200 bg-rose-50/50' : 'border-slate-100 bg-slate-50/50'}`}>
      <p className="text-[11px] text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-slate-900 tabular-nums">{value}</p>
      {sub && <p className={`text-[11px] ${tone === 'rose' ? 'text-rose-700' : 'text-slate-500'}`}>{sub}</p>}
    </div>
  );
}
